import {call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {captureException, withScope} from '@sentry/browser';
import basketApi from '../api/basket.api';
import * as ActionTypes from '../actions/types.action';
import {removeBasketItem, updateBasketItem} from '../actions/basket.action';
import {closeBoxPicker, openBoxPicker} from '../actions/modal.action';
import {getBearerHeader} from '../helpers/api.helper';
import {createHashMap, updateCurrentBasket} from '../helpers/data.helper';
import {
  findPickerLine,
  getBoxPickFailureReason,
  isTransientBoxLine,
  withoutPickerData,
} from '../helpers/boxes.helper';
import {
  BOX_PICK_FAILURE_REASON_REQUEST,
  BOX_PICKER_COPY,
  BOX_PICKER_SOURCE,
  ERRORS,
  ROUTES,
} from '../constants/constants';
import {putSyncedBasketPageItems} from './updateBasketItem.saga';

const getAuth = state => state.auth;
const getStoreBasket = state => state.basket;
const getBasketItems = state => state.basketItems;
const getRoute = state => state.route;
const getBoxPicker = state => state.modal && state.modal.boxPicker;

const findSavedLine = (basket, itemId) =>
  ((basket && basket.items) || []).find(
    line => line && line.itemId === itemId && !!line.uuid
  ) || null;

function* getHeaders() {
  const storeToken = yield select(getAuth);
  return getBearerHeader(undefined, storeToken);
}

// Store a box response the way updateBasket.saga does, without the
// response-only picker data, and keep the basket page's lines in step.
export function* putBoxResponse(basket, itemId) {
  const cleaned = withoutPickerData(basket);
  const storeBasket = yield select(getStoreBasket);
  const currentRoute = yield select(getRoute);
  const isBasketPage = currentRoute === ROUTES.BASKET;
  const savedLine = findSavedLine(cleaned, itemId);
  const newBasket =
    isBasketPage && storeBasket && savedLine
      ? updateCurrentBasket(false, storeBasket, cleaned, savedLine.uuid)
      : cleaned;
  yield put({type: ActionTypes.UPDATE_BASKET_SUCCEEDED, basket: newBasket});
  yield put({
    type: ActionTypes.SET_BASKET_HASHMAP_SUCCEEDED,
    basketHashMap: createHashMap(newBasket.items),
  });
  if (isBasketPage) {
    const basketItems = yield select(getBasketItems);
    yield putSyncedBasketPageItems(storeBasket, basketItems, newBasket);
  }
}

function* handleRequestError(e, action) {
  const data = e && e.response && e.response.data;
  if (
    (data && data.error === ERRORS.EXPIRED) ||
    (e && e.code === ERRORS.NEED_AUTH)
  ) {
    yield put({type: ActionTypes.ERROR_LOGIN_EXPIRED});
  }
  withScope(scope => {
    scope.setExtra('action', action);
    captureException(e);
  });
}

export function* pickBoxes(action) {
  const {itemId, boxNumbers} = action;
  try {
    const headers = yield call(getHeaders);
    const basket = yield call(basketApi.pickBoxes, itemId, boxNumbers, headers);
    const pickerLine = findPickerLine(basket, itemId);
    yield call(putBoxResponse, basket, itemId);
    if (isTransientBoxLine(pickerLine)) {
      // Nothing was saved: keep the picker open with the fresh list.
      const message =
        (pickerLine.stockCheck && pickerLine.stockCheck.message) || null;
      yield put({
        type: ActionTypes.PICK_BOXES_FAILED,
        itemId,
        message,
        reason: getBoxPickFailureReason(message),
        refusedBoxNumbers: pickerLine.boxNumbers || [],
        boxes: pickerLine.boxes,
      });
      return;
    }
    yield put({
      type: ActionTypes.PICK_BOXES_SUCCEEDED,
      itemId,
      line: findSavedLine(basket, itemId),
    });
    yield put(closeBoxPicker());
  } catch (e) {
    yield put({
      type: ActionTypes.PICK_BOXES_FAILED,
      itemId,
      message: BOX_PICKER_COPY.REQUEST_ERROR,
      reason: BOX_PICK_FAILURE_REASON_REQUEST,
    });
    yield call(handleRequestError, e, action);
  }
}

export function* peekBoxes(action) {
  const {itemId} = action;
  const source = action.source || BOX_PICKER_SOURCE.CHANGE;
  try {
    const headers = yield call(getHeaders);
    const basket = yield call(basketApi.peekBoxes, itemId, headers);
    const pickerLine = findPickerLine(basket, itemId);
    // The peek also refreshed the holds, so store what came back.
    yield call(putBoxResponse, basket, itemId);
    if (!pickerLine) {
      yield put({type: ActionTypes.PEEK_BOXES_FAILED, itemId});
      yield put(
        openBoxPicker({
          itemId,
          source,
          line: findSavedLine(basket, itemId),
          boxes: null,
          error: BOX_PICKER_COPY.PEEK_ERROR,
          removeOnClose: false,
        })
      );
      return;
    }
    yield put({type: ActionTypes.PEEK_BOXES_SUCCEEDED, itemId});
    yield put(
      openBoxPicker({
        itemId,
        source,
        line: findSavedLine(basket, itemId) || pickerLine,
        boxes: pickerLine.boxes,
        removeOnClose: false,
      })
    );
  } catch (e) {
    yield put({type: ActionTypes.PEEK_BOXES_FAILED, itemId});
    yield put(
      openBoxPicker({
        itemId,
        source,
        line: null,
        boxes: null,
        error: BOX_PICKER_COPY.PEEK_ERROR,
        removeOnClose: false,
      })
    );
    yield call(handleRequestError, e, action);
  }
}

// PUT replaces the line's boxes; removing the last box removes the line.
export function* updateLineBoxes(action) {
  const {uuid, boxNumbers} = action;
  if (!uuid) {
    return;
  }
  if (!boxNumbers || boxNumbers.length === 0) {
    yield put(removeBasketItem(uuid));
    return;
  }
  yield put(updateBasketItem(uuid, {boxNumbers}));
}

// Closed without picking: drop the line the add just created (the customer
// pressed Add to choose a box, not to buy it unseen). Lines that were already
// in the basket, or that were opened by a peek, are left alone.
export function* dismissBoxPicker() {
  const picker = yield select(getBoxPicker);
  if (
    picker &&
    picker.removeOnClose &&
    !picker.picking &&
    picker.line &&
    picker.line.uuid
  ) {
    yield put(removeBasketItem(picker.line.uuid));
  }
  yield put(closeBoxPicker());
}

export default function* boxes() {
  yield takeLatest(ActionTypes.PICK_BOXES_REQUESTED, pickBoxes);
  yield takeLatest(ActionTypes.PEEK_BOXES_REQUESTED, peekBoxes);
  yield takeEvery(ActionTypes.UPDATE_LINE_BOXES_REQUESTED, updateLineBoxes);
  yield takeEvery(ActionTypes.DISMISS_BOX_PICKER_REQUESTED, dismissBoxPicker);
}
